import {
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Textarea,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import YesNoRadioGroup from "../dashboard/application/YesNoRadioGroup";
import { REQUIRED_FIELD, SSNMask } from "@/lib/constants";
import PhoneNumberInput from "../forms/PhoneNumberInput";
import { InputMask } from "@react-input/mask";
import DateOfBirthInput from "../forms/DateOfBirthInput";
import LocationSelect from "../LocationSelect";
import CountrySelect from "../forms/CountrySelect";
import CurrencyInput from "react-currency-input-field";
import GoogleAddressInput from "../dashboard/GoogleAddressInput";
import ListFieldManager from "../forms/ListFieldManager";
import ConditonsInput from "../forms/ConditionsInput";
import { useContext } from "react";
import { FormContext } from "@/providers/form";
import RequiredIndicator from "./RequiredIndicator";
import { InputWrapper } from "../forms/JailTextInputWrapper";
import TemplateManager from "./TemplateManager";

const custom_field_width: {
  [key: string]: string;
} = {
  ConditionsInput: "100%",
  Textarea: "100%",
  Radio: "100%",
  Template: "100%",
};

const SelfServeFields = ({ field }: { field: any }) => {
  const { values, saveForm } = useContext(FormContext);

  const { control, register, watch, setValue } = useForm<any>({
    defaultValues: {
      [field.databaseKey]: values[field.databaseKey],
    },
  });

  const watchValue = watch(field.databaseKey);

  return (
    <>
      <FormControl key={field.label} py="12px">
        {field.label && (
          <>
            <FormLabel>
              {field.label}
              <RequiredIndicator value={values[field.databaseKey]} />
            </FormLabel>
            {field.description && (
              <FormHelperText mb={2}>{field.description}</FormHelperText>
            )}
          </>
        )}

        <Flex
          width={{
            base: "100%",
            lg: custom_field_width[field.type] || "50%",
          }}
        >
          {field.type === "YesNoRadioGroup" && (
            <YesNoRadioGroup
              fieldName={field.databaseKey || field.label}
              control={control}
            />
          )}
          {field.type === "Input" && (
            <InputWrapper
              name={field.databaseKey}
              control={control}
              rules={{ required: REQUIRED_FIELD }}
              placeholder={field.placeholder}
              onBlur={(ev: any) => {
                saveForm({
                  [field.databaseKey]: ev.target.value || watchValue,
                });
              }}
            />
          )}
          {field.type === "Select" && (
            <Select
              defaultValue={values[field.databaseKey] || ""}
              {...register(field.databaseKey, {
                required: REQUIRED_FIELD,
                onBlur: (ev: any) => {
                  saveForm({
                    [field.databaseKey]: ev.target.value,
                  });
                },
              })}
            >
              <option value="" disabled>
                Choose One
              </option>
              {field.options.map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          )}
          {field.type === "PhoneNumberInput" && (
            <PhoneNumberInput
              register={register}
              fieldName={field.databaseKey}
            />
          )}
          {field.type === "SSNInput" && (
            <Controller
              name={field.databaseKey}
              control={control}
              rules={{ required: REQUIRED_FIELD }}
              render={({ field: formField }) => (
                <Input
                  type="text"
                  placeholder="111-22-3333"
                  as={InputMask}
                  mask={SSNMask.mask}
                  replacement={SSNMask.replacement}
                  value={formField.value}
                  onChange={(ev) => formField.onChange(ev.target.value)}
                  onBlur={(ev) => {
                    saveForm({
                      [field.databaseKey]: ev.target.value.replace(/\D/g, ""),
                    });
                  }}
                />
              )}
            />
          )}
          {field.type === "DateOfBirthInput" && (
            <DateOfBirthInput
              register={register}
              fieldName={field.databaseKey}
            />
          )}
          {field.type === "StateSelect" && (
            <Controller
              name={field.databaseKey}
              control={control}
              rules={{ required: REQUIRED_FIELD }}
              render={({ field: formField }) => (
                <LocationSelect
                  valueKey="name"
                  width="100%"
                  value={formField.value}
                  onChange={(val) => {
                    formField.onChange(val);
                    saveForm({
                      [field.databaseKey]: val,
                    });
                  }}
                />
              )}
            />
          )}
          {field.type === "CountrySelect" && (
            <Controller
              name={field.databaseKey}
              control={control}
              rules={{ required: REQUIRED_FIELD }}
              render={({ field: formField }) => (
                <CountrySelect
                  value={formField.value}
                  onChange={(val) => {
                    formField.onChange(val);
                    saveForm({
                      [field.databaseKey]: val,
                    });
                  }}
                />
              )}
            />
          )}
          {field.type === "FullDate" && (
            <Input
              placeholder={field.placeholder || "12/13/1991"}
              as={InputMask}
              mask="__/__/____"
              replacement={{ _: /\d/ }}
              type="text"
              {...register(field.databaseKey, {
                required: REQUIRED_FIELD,
                onBlur: (ev: any) => {
                  saveForm({
                    [field.databaseKey]: ev.target.value,
                  });
                },
              })}
            />
          )}
          {field.type === "NumberInput" && (
            <Input
              type="number"
              placeholder={field.placeholder || "2"}
              min="0"
              {...register(field.databaseKey, {
                required: REQUIRED_FIELD,
                valueAsNumber: true,
                min: 0,
                onBlur: (ev: any) => {
                  saveForm({
                    [field.databaseKey]: ev.target.value,
                  });
                },
              })}
            />
          )}
          {field.type === "DollarInput" && (
            <InputGroup>
              <InputLeftElement pointerEvents="none">$</InputLeftElement>
              <Input
                as={CurrencyInput}
                decimalsLimit={field.decimalsLimit || 2}
                value={watch(field.databaseKey)}
                onValueChange={(value?: string) =>
                  setValue(
                    field.databaseKey,
                    Number((value || "").replace(/[^0-9.-]+/g, ""))
                  )
                }
                w="auto"
                onBlur={(ev) => {
                  saveForm({
                    [field.databaseKey]: Number(
                      (ev.target.value || "").replace(/[^0-9.-]+/g, "")
                    ),
                  });
                }}
                placeholder={field.placeholder || "11.25"}
              />
            </InputGroup>
          )}
          {field.type === "AddressAutocomplete" && (
            <Controller
              name={field.databaseKey}
              control={control}
              rules={{
                required: REQUIRED_FIELD,
              }}
              render={({ field: formField }) => (
                <GoogleAddressInput
                  value={formField.value}
                  onChange={(val) => {
                    formField.onChange(val);
                    saveForm({
                      [field.databaseKey]: val,
                    });
                  }}
                />
              )}
            />
          )}
          {field.type === "MultiCheckbox" && (
            <Controller
              name={field.databaseKey}
              control={control}
              rules={{ required: REQUIRED_FIELD }}
              render={({ field: { value = [], onChange } }) => {
                const handleCheckboxChange = (checkboxValue: string) => {
                  const updatedValues = value.includes(checkboxValue)
                    ? value.filter((val: string) => val !== checkboxValue)
                    : [...value, checkboxValue];
                  onChange(updatedValues);
                  saveForm({
                    [field.databaseKey]: updatedValues,
                  });
                };

                return (
                  <Stack direction="column" gap="12px">
                    {field.options.map((option: string) => (
                      <Checkbox
                        key={option}
                        isChecked={value.includes(option)}
                        onChange={() => handleCheckboxChange(option)}
                      >
                        {option}
                      </Checkbox>
                    ))}
                  </Stack>
                );
              }}
            />
          )}
          {field.type === "Radio" && (
            <Controller
              name={field.databaseKey}
              control={control}
              rules={{ required: REQUIRED_FIELD }}
              render={({ field: { value, onChange } }) => (
                <RadioGroup
                  onChange={(val) => {
                    onChange(val);
                    saveForm({
                      [field.databaseKey]: val,
                    });
                  }}
                  name={field.databaseKey}
                  value={value}
                >
                  <Stack>
                    {field.options.map((option: string) => (
                      <Radio key={option} value={option}>
                        {option}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              )}
            />
          )}
          {field.type === "Textarea" && (
            <Textarea
              placeholder={field.placeholder || "Enter more information..."}
              {...register(field.databaseKey, {
                required: REQUIRED_FIELD,
                onBlur: (ev: any) => {
                  saveForm({
                    [field.databaseKey]: ev.target.value,
                  });
                },
              })}
            />
          )}
          {field.type === "ConditionsInput" && (
            <Controller
              name={field.databaseKey}
              control={control}
              rules={{ required: REQUIRED_FIELD }}
              render={({ field: { value, onChange } }) => (
                <ConditonsInput
                  value={value}
                  onChange={(val: string[]) => {
                    saveForm({
                      [field.databaseKey]: val,
                    });
                    onChange(val);
                  }}
                />
              )}
            />
          )}
          {field.type === "Template" && (
            <TemplateManager template={field.template} />
          )}
        </Flex>
      </FormControl>
      {field.type === "ListManager" && (
        <ListFieldManager field={field} control={control} register={register} />
      )}
    </>
  );
};

export default SelfServeFields;
